import React, { useRef, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import { Spin, Empty } from 'antd';
import { useTranslation } from 'react-i18next';

const BatteryChartPanel = forwardRef(function BatteryChartPanel(
  { data, chartData, measureMarkers, eventMarkers, onChartClick, onChartReady, loading },
  ref
) {
  const { t } = useTranslation();
  const chartInstanceRef = useRef(null);

  // Keep the latest onChartClick / timestamps for the zrender click handler,
  // which is bound once on chart ready.
  const onChartClickRef = useRef(onChartClick);
  onChartClickRef.current = onChartClick;
  const timestampsRef = useRef(chartData && chartData.timestamps ? chartData.timestamps : []);
  timestampsRef.current = chartData && chartData.timestamps ? chartData.timestamps : [];

  // Expose chart instance to parent via ref
  useImperativeHandle(ref, () => ({
    getEchartsInstance: () => chartInstanceRef.current ? chartInstanceRef.current.getEchartsInstance() : null,
  }));

  // Build chart option from data
  const chartOption = useMemo(() => {
    if (!chartData) return null;
    return buildBatteryDashboardOption(chartData, measureMarkers, eventMarkers, t);
  }, [chartData, measureMarkers, eventMarkers, t]);

  // Bind a zrender-level click so a marker can be placed anywhere on the chart,
  // not only exactly on a data point (series use symbol:'none').
  const handleChartReady = useCallback((chart) => {
    const zr = chart.getZr();
    zr.off('click');
    zr.on('click', (event) => {
      const cb = onChartClickRef.current;
      if (!cb) return;

      const pointInPixel = [event.offsetX, event.offsetY];
      // All grids share the same horizontal layout, so xAxisIndex 0 maps any click.
      const grid = chart.convertFromPixel({ xAxisIndex: 0 }, pointInPixel);
      if (!grid) return;

      const timestamps = timestampsRef.current;
      let dataIndex = Math.round(grid[0]);
      if (dataIndex < 0) dataIndex = 0;
      if (dataIndex > timestamps.length - 1) dataIndex = timestamps.length - 1;
      if (dataIndex < 0) return;

      const timestamp = timestamps[dataIndex];
      cb({ dataIndex, name: timestamp, value: [timestamp] });
    });

    if (onChartReady) onChartReady(chart);
  }, [onChartReady]);

  if (loading) {
    return (
      <div className="battery-analysis-loading">
        <Spin size="large" />
        <p>{t('Loading data...')}</p>
      </div>
    );
  }

  if (!chartOption) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Empty description={t('Select device and date range to analyze')} />
      </div>
    );
  }

  return (
    <div className="battery-analysis-chart-container">
      <ReactECharts
        ref={chartInstanceRef}
        option={chartOption}
        style={{ width: '100%', height: '100%' }}
        notMerge={true}
        lazyUpdate={true}
        onChartReady={handleChartReady}
      />
    </div>
  );
});

/**
 * Build the cascaded ECharts option for the battery dashboard.
 * Tiers: Status, Pack Voltage, Cell Voltage (Max/Min), SOC/Current, Temperatures, Cell Difference
 */
function buildBatteryDashboardOption(chartData, measureMarkers, eventMarkers, t) {
  const {
    timestamps,
    packVoltage,
    maxCellVoltage,
    minCellVoltage,
    soc,
    current,
    temperatures,
    temperatureKeys,
    cellDifference,
    status,
  } = chartData;

  if (!timestamps || timestamps.length === 0) {
    return { title: { text: t('No data available'), left: 'center', top: 'center' } };
  }

  // Color palette for temperature series
  const tempColors = [
    '#e6194b', '#3cb44b', '#4363d8', '#f58231', '#911eb4',
    '#42d4f4', '#f032e6', '#bfef45', '#fabebe', '#469990',
    '#e6beff', '#9A6324', '#800000', '#aaffc3', '#808000',
    '#000075', '#ffe119', '#dcbeff'
  ];

  // Status enum: 0 Static / 1 Charging / 2 Discharging / 3 Fault / 4 Sleep / 5 Heating
  const statusLabels = {
    0: t('Static'),
    1: t('Charging'),
    2: t('Discharging'),
    3: t('Fault'),
    4: t('Sleep'),
    5: t('Heating'),
  };

  // Common x-axis config
  const xAxisBase = {
    type: 'category',
    data: timestamps,
    axisLabel: { show: false },
    axisTick: { show: false },
  };

  // Grid layout for 6 tiers
  const grids = [
    { left: 60, right: 40, top: 30, height: '8%' },     // Status
    { left: 60, right: 40, top: '17%', height: '12%' }, // Pack Voltage
    { left: 60, right: 40, top: '33%', height: '18%' }, // Cell Voltage (Max/Min)
    { left: 60, right: 40, top: '55%', height: '12%' }, // SOC + Current
    { left: 60, right: 40, top: '70%', height: '12%' }, // Temperature
    { left: 60, right: 40, top: '85%', height: '9%' },  // Cell Difference
  ];

  // Build xAxes (one per grid)
  const xAxes = grids.map((_, idx) => ({
    ...xAxisBase,
    gridIndex: idx,
    axisLabel: idx === grids.length - 1 ? { show: true, rotate: 30, fontSize: 10 } : { show: false },
  }));

  // Build yAxes
  const yAxes = [
    {
      gridIndex: 0,
      name: t('Status'),
      nameTextStyle: { fontSize: 10 },
      type: 'value',
      min: 0,
      max: 5,
      interval: 1,
      axisLabel: {
        fontSize: 9,
        formatter: (v) => (statusLabels[v] != null ? statusLabels[v] : ''),
      },
      splitLine: { show: true },
    },
    { gridIndex: 1, name: t('Pack V'), nameTextStyle: { fontSize: 10 }, splitLine: { show: true } },
    { gridIndex: 2, name: t('Cell V'), nameTextStyle: { fontSize: 10 }, scale: true, splitLine: { show: true } },
    { gridIndex: 3, name: t('SOC(%)'), nameTextStyle: { fontSize: 10 }, splitLine: { show: true } },
    { gridIndex: 3, name: t('Current(A)'), nameTextStyle: { fontSize: 10 }, position: 'right', splitLine: { show: false } },
    { gridIndex: 4, name: t('Temp(°C)'), nameTextStyle: { fontSize: 10 }, splitLine: { show: true } },
    { gridIndex: 5, name: t('Diff(mV)'), nameTextStyle: { fontSize: 10 }, splitLine: { show: true } },
  ];

  // Build series
  const series = [];

  // Tier 1: Status (step chart)
  if (status) {
    series.push({
      name: t('Status'),
      type: 'line',
      step: 'start',
      xAxisIndex: 0,
      yAxisIndex: 0,
      data: status,
      symbol: 'none',
      lineStyle: { width: 1.5, color: '#16a085' },
      itemStyle: { color: '#16a085' },
      areaStyle: { opacity: 0.15, color: '#16a085' },
      tooltip: {
        valueFormatter: (v) => (statusLabels[v] != null ? statusLabels[v] : v),
      },
    });
  }

  // Tier 2: Pack Voltage
  if (packVoltage) {
    series.push({
      name: t('Pack Voltage'),
      type: 'line',
      xAxisIndex: 1,
      yAxisIndex: 1,
      data: packVoltage,
      symbol: 'none',
      lineStyle: { width: 1.5 },
      itemStyle: { color: '#2c3e50' },
    });
  }

  // Tier 3: Cell Voltage - two lines (Max / Min)
  if (maxCellVoltage) {
    series.push({
      name: t('Max Cell Voltage'),
      type: 'line',
      xAxisIndex: 2,
      yAxisIndex: 2,
      data: maxCellVoltage,
      symbol: 'none',
      lineStyle: { width: 1.5 },
      itemStyle: { color: '#e74c3c' },
    });
  }
  if (minCellVoltage) {
    series.push({
      name: t('Min Cell Voltage'),
      type: 'line',
      xAxisIndex: 2,
      yAxisIndex: 2,
      data: minCellVoltage,
      symbol: 'none',
      lineStyle: { width: 1.5 },
      itemStyle: { color: '#3498db' },
    });
  }

  // Tier 4: SOC
  if (soc) {
    series.push({
      name: t('SOC'),
      type: 'line',
      xAxisIndex: 3,
      yAxisIndex: 3,
      data: soc,
      symbol: 'none',
      lineStyle: { width: 1.5 },
      itemStyle: { color: '#27ae60' },
      areaStyle: { opacity: 0.1 },
    });
  }

  // Tier 4: Current (secondary Y axis)
  if (current) {
    series.push({
      name: t('Current'),
      type: 'line',
      xAxisIndex: 3,
      yAxisIndex: 4,
      data: current,
      symbol: 'none',
      lineStyle: { width: 1.5 },
      itemStyle: { color: '#e67e22' },
    });
  }

  // Tier 5: Temperatures
  if (temperatures && temperatures.length > 0) {
    temperatures.forEach((tempData, idx) => {
      const name = temperatureKeys && temperatureKeys[idx] ? temperatureKeys[idx] : `T${idx + 1}`;
      series.push({
        name,
        type: 'line',
        xAxisIndex: 4,
        yAxisIndex: 5,
        data: tempData,
        symbol: 'none',
        lineStyle: { width: 1 },
        itemStyle: { color: tempColors[idx % tempColors.length] },
      });
    });
  }

  // Tier 6: Cell Difference
  if (cellDifference) {
    series.push({
      name: t('Cell Difference'),
      type: 'line',
      xAxisIndex: 5,
      yAxisIndex: 6,
      data: cellDifference,
      symbol: 'none',
      lineStyle: { width: 1.5 },
      itemStyle: { color: '#8e44ad' },
      areaStyle: { opacity: 0.1 },
    });
  }

  // Add measure markers as mark lines on the first series (spans the Status tier).
  if (measureMarkers && measureMarkers.length > 0 && series.length > 0) {
    const markLineData = measureMarkers.map(m => ({
      xAxis: m.timestamp,
      label: { formatter: m.label, position: 'start' },
      lineStyle: { color: '#27ae60', type: 'dashed', width: 2 },
    }));
    series[0].markLine = {
      silent: false,
      symbol: ['none', 'arrow'],
      data: markLineData,
    };
  }

  // Add event markers
  if (eventMarkers && eventMarkers.length > 0 && series.length > 1) {
    const eventMarkLineData = eventMarkers.map(m => ({
      xAxis: m.timestamp,
      label: { formatter: m.type, position: 'end', fontSize: 9 },
      lineStyle: {
        color: m.severity === 'critical' ? '#e74c3c' : '#f39c12',
        type: 'solid',
        width: 2,
      },
    }));
    series[1].markLine = {
      silent: false,
      symbol: ['none', 'triangle'],
      data: eventMarkLineData,
    };
  }

  const allGridIndices = grids.map((_, idx) => idx);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross', link: [{ xAxisIndex: 'all' }] },
    },
    axisPointer: { link: [{ xAxisIndex: 'all' }] },
    toolbox: {
      feature: {
        dataZoom: { yAxisIndex: false },
        restore: {},
      },
      right: 40,
    },
    dataZoom: [
      { type: 'inside', xAxisIndex: allGridIndices, start: 0, end: 100 },
      { type: 'slider', xAxisIndex: allGridIndices, bottom: 5, height: 20 },
    ],
    grid: grids,
    xAxis: xAxes,
    yAxis: yAxes,
    series: series,
  };
}

export default BatteryChartPanel;
